import { useEffect } from 'react';
import { Briefcase, Loader2, Clock, Heart, Palette, FileText, Users, ArrowRight, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import GameCard from '../components/game/GameCard';
import GameGrid from '../components/game/GameGrid';
import { useGamesStore } from '../store/useGamesStore';
import { LINE_GRADIENT } from '../constants/gradients';

export default function MyGamesPage() {
  const {
    isLoadingCatalog,
    catalogError,
    hasLoadedCatalog,
    fetchCatalog,
    getMyGames,
    privateGames,
    unpublishedGames,
    userTeams,
    teamGames,
  } = useGamesStore();

  // Fetch catalog on mount
  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const myGames = getMyGames();
  
  // TODO: Track recently played, favorites, and purchased games in local storage or backend
  // For now, we'll show empty states for these sections
  const recentlyPlayedGames: any[] = [];
  const favoriteGames: any[] = [];
  const purchasedGames: any[] = [];

  // Loading skeleton component
  const GameCardSkeleton = () => (
    <div className="card overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      </div>
    </div>
  );

  const GridSkeleton = ({ count = 8 }: { count?: number }) => (
    <GameGrid>
      {Array.from({ length: count }).map((_, i) => (
        <GameCardSkeleton key={i} />
      ))}
    </GameGrid>
  );

  // Error state
  if (catalogError && !hasLoadedCatalog) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-red-500 mb-4">Failed to load games</h2>
        <p className="text-muted mb-4">{catalogError}</p>
        <button
          onClick={() => fetchCatalog({}, true)}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* SVG Gradient Definition */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'rgb(252, 131, 2)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FF00CF', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>

      {/* Recently Played Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Clock 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Recently Played</h2>
          </div>
          {recentlyPlayedGames.length > 0 && (
            <Link
              to="/search?filter=recent"
              className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              See All
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : recentlyPlayedGames.length > 0 ? (
          <GameGrid>
            {recentlyPlayedGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <Clock className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No recently played games yet</p>
          </div>
        )}
      </section>

      {/* Favorites Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Heart 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
                fill: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Favorites</h2>
          </div>
          {favoriteGames.length > 0 && (
            <Link
              to="/search?filter=favorites"
              className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              See All
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : favoriteGames.length > 0 ? (
          <GameGrid>
            {favoriteGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <Heart className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No favorite games yet</p>
          </div>
        )}
      </section>

      {/* Purchased Games Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShoppingBag 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Purchased Games</h2>
          </div>
          {purchasedGames.length > 0 && (
            <Link
              to="/search?filter=purchased"
              className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              See All
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : purchasedGames.length > 0 ? (
          <GameGrid>
            {purchasedGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <ShoppingBag className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No purchased games yet</p>
          </div>
        )}
      </section>

      {/* My Creations Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Palette 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Creations</h2>
          </div>
          {privateGames.length > 0 && (
            <Link
              to="/search?filter=my-creations"
              className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              See All
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : privateGames.length > 0 ? (
          <GameGrid>
            {privateGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <Palette className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No personal creations yet</p>
          </div>
        )}
      </section>

      {/* Unpublished Drafts Section */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText 
              className="w-7 h-7" 
              style={{
                stroke: 'url(#icon-gradient)',
              }}
            />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Unpublished Drafts</h2>
          </div>
          {unpublishedGames.length > 0 && (
            <Link
              to="/search?filter=unpublished"
              className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              See All
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {isLoadingCatalog ? (
          <GridSkeleton count={4} />
        ) : unpublishedGames.length > 0 ? (
          <GameGrid>
            {unpublishedGames.slice(0, 4).map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No unpublished drafts</p>
          </div>
        )}
      </section>

      {/* Team Games Sections */}
      {userTeams && userTeams.length > 0 && userTeams
        .filter(team => !team.is_personal)
        .map((team) => {
        const games = teamGames[team.id] || [];
        
        return (
          <section key={team.id}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {team.avatar_url ? (
                  <img
                    src={team.avatar_url}
                    alt={team.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FCB902] to-[#FF00CF] flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                )}
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {team.name}'s Games
                </h2>
              </div>
              {games.length > 0 && (
                <Link
                  to={`/search?team=${team.id}`}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  See All
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            {isLoadingCatalog ? (
              <GridSkeleton count={4} />
            ) : games.length > 0 ? (
              <GameGrid>
                {games.slice(0, 4).map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </GameGrid>
            ) : (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <Users className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">No games from {team.name} yet</p>
              </div>
            )}
          </section>
        );
      })}

      {/* Loading indicator when refreshing */}
      {isLoadingCatalog && hasLoadedCatalog && (
        <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      )}
    </div>
  );
}
